import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';

import { createMortgage } from '../../../../services/mutations';
import { getOrderProperties } from '../../../../services/queries';
import { reviseOrderLedger } from 'views/orders/accounting/services/mutations';

type addMortgageForm = {
  estateId: string | null;
  loan_number: number | null;
  id: string;
};
export const useAddMortgage = (open: boolean, onClose: () => void) => {
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();
  const { t } = useTranslation();
  const queryCache = useQueryClient();
  const methods = useForm<addMortgageForm>({
    mode: 'onChange',
    defaultValues: {
      id: orderId,
      loan_number: null,
      estateId: null,
    },
  });
  const getOrder = useQuery(
    ['mortgage-order-properties', orderId],
    () => getOrderProperties({ id: orderId }),
    {
      enabled: !!orderId && open,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
  const { mutate: reviseOrderLedgerRequest } = useMutation(reviseOrderLedger);
  const { mutate: createMortgageMutation, ...createMortgageReponse } = useMutation(createMortgage, {
    onSuccess: () => {
      queryCache.invalidateQueries(['order-mortgages', orderId],);
      queryCache.invalidateQueries(["properties-mortgage-payofss", orderId]);
      showAlert(t('payoffs:mortgage-created'), 'success');
      reviseOrderLedgerRequest(orderId);
      onClose();
    },
  });

  const properties = getOrder.data?.getOrder.estates || [];

  useEffect(() => {
    if (properties.length > 0) {
      methods.reset({
        id: orderId,
        loan_number: null,
        estateId: properties[0]._id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  return {
    properties,
    methods,
    createMortgageMutation,
    createMortgageReponse,
  };
};
